import React from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import lang from '../../../languages/en.json';
import { isMobileDevice, renderPeerGroupRankCell } from '../../../utils/appUtils';
import { createKpiRenderers, renderValueOrRisk, GridRenderContext } from '../cellRenderers';
import pagesStandardStyles from '../../../pages/Pages.module.css';

// Define which sections and cards should display the benchmark info icon in the header
const SECTIONS_WITH_BENCHMARK_INFO_ICON: Set<string> = new Set(['Assortment and Pricing']);
const CARDS_WITH_BENCHMARK_INFO_ICON: Set<string> = new Set(['B2B']);

/**
 * Creates column definitions for KPI DataGrid
 * @param context - Grid render context with drawer state setters
 * @param showPeerGroup - Whether to include peer group columns
 * @param isMultiStoreEnabled - Whether multi-store mode is on (changes peer group rank header text)
 * @param sectionTitle - Title of the KPI grid section (used to target specific benchmark headers)
 * @param cardName - Card name of the KPI grid (used to target all sections of a card)
 * @returns Array of column definitions
 */
export const createKpiColumns = (
  context: GridRenderContext,
  showPeerGroup: boolean,
  isMultiStoreEnabled = false,
  sectionTitle = '',
  cardName = '',
): GridColDef[] => {
  // Determine if benchmark info icon should be shown based on section/card and multi-store status
  const showBenchmarkInfoIcon =
    isMultiStoreEnabled &&
    (SECTIONS_WITH_BENCHMARK_INFO_ICON.has(sectionTitle) ||
      CARDS_WITH_BENCHMARK_INFO_ICON.has(cardName));
  // Create renderers with context - factory pattern prevents re-creation on every render
  const renderers = createKpiRenderers(context);

  return [
    {
      field: 'kpi',
      headerName: lang.dataGrid.columns.kpi,
      flex: 1.5,
      minWidth: 260,
      renderCell: renderers.renderKpiNameCell,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'moreInfo',
      headerName: lang.dataGrid.columns.moreInfo,
      flex: 0.8,
      minWidth: 100,
      renderCell: renderers.renderMoreInfoCell,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'kpiGoal',
      headerName: lang.dataGrid.columns.goal,
      flex: 0.6,
      minWidth: 80,
      align: 'right',
      headerAlign: 'left',
      renderCell: params => {
        // Special handling for SHRW KPI - treat as number instead of percentage
        if (params.row.kpiCode === 'SHRW') {
          const modifiedParams = {
            ...params,
            row: {
              ...params.row,
              kpiFormat: 'NUM', // Override format to NUM to suppress %
            },
          };
          return renderValueOrRisk(modifiedParams, params.row.kpiGoal, context);
        }
        return renderValueOrRisk(params, params.row.kpiGoal, context);
      },
    },
    ...(!isMobileDevice()
      ? ([
          {
            field: 'takeActionTitle',
            headerName: lang.pageContent.columnTakeAction,
            flex: 2,
            minWidth: 150,
            renderCell: renderers.renderTakeActionCell,
            align: 'left' as const,
            headerAlign: 'left' as const,
          },
        ] as GridColDef[])
      : []),
    {
      field: 'priorKpi',
      headerName: lang.dataGrid.columns.prior,
      flex: 0.8,
      minWidth: 100,
      renderCell: params => renderValueOrRisk(params, params.row.priorKpiAmount, context),
      align: 'right',
      headerAlign: 'left',
    },
    {
      field: 'currentKpi',
      headerName: lang.dataGrid.columns.current,
      flex: 0.8,
      minWidth: 100,
      renderCell: params => renderValueOrRisk(params, params.row.currentKpiAmount, context),
      align: 'right',
      headerAlign: 'left',
    },
    ...(showPeerGroup
      ? ([
          {
            field: 'peerGroupRank',
            // Change header text to MS based on multi-store status
            headerName: isMultiStoreEnabled
              ? lang.pageContent.msGroupRank
              : lang.pageContent.peerGroupRank,
            flex: 0.8,
            minWidth: 130,
            renderCell: renderPeerGroupRankCell,
            align: 'right' as const,
            headerAlign: 'left' as const,
          },
          {
            field: 'benchmark',
            headerName: lang.pageContent.percentBenchmark,
            flex: 0.8,
            minWidth: 130,
            renderCell: params => renderValueOrRisk(params, params.row.benchmark, context),
            align: 'right' as const,
            headerAlign: 'left' as const,
            // Conditionally add benchmark info icon to header if multi-store is enabled and section/card matches criteria
            ...(showBenchmarkInfoIcon && {
              renderHeader: () => (
                <div className={pagesStandardStyles.benchmarkHeader}>
                  <span>80%</span>
                  <span className={pagesStandardStyles.benchmarkHeaderRow}>
                    {lang.pageContent.benchmark}
                    <Tooltip
                      title={lang.pageContent.benchmarkPerStoreAveragesNote}
                      arrow
                      enterTouchDelay={0}
                      leaveTouchDelay={6000}>
                      <InfoOutlinedIcon
                        fontSize="small"
                        className={pagesStandardStyles.benchmarkInfoIconHeader}
                      />
                    </Tooltip>
                  </span>
                </div>
              ),
            }),
          },
        ] as GridColDef[])
      : []),
    {
      field: 'kpiProgress',
      headerName: '',
      flex: 1,
      minWidth: 50,
      maxWidth: 150,
      renderCell: renderers.renderKpiProgressIcon,
    },
  ];
};
